<template>
    <div class="bg-gray-100">
        <!-- Hero topo -->
        <div class="w-full py-10 sm:py-24"
            style="background: linear-gradient(90deg, rgba(14, 57, 108, 1) 0%, rgba(1, 30, 65, 1) 100%);">
            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-40 space-y-16">
                <div class="text-center md:text-left mx-auto text-white">
                    <h1 class="text-2xl sm:text-3xl lg:text-5xl font-regular mb-3 sm:mb-4">Encontre o<br>produto
                        ideal<br>para sua obra!</h1>
                </div>
            </div>
        </div>

        <!-- Placeholder para evitar footer grudado -->
        <div v-if="!loaded" class="bg-white h-[500px]"></div>

        <!-- Conteúdo principal -->
        <div v-else class="container max-w-[1200px] mx-auto px-4 py-5">
            <div class="mt-5">
                <!-- Toggle mobile -->
                <div v-if="isMobile"
                    class="md:hidden block border border-gray-300 rounded-lg text-[#343434]/50 py-3 cursor-pointer mb-10 text-center"
                    @click="toggleFilters">
                    <div class="flex justify-center items-center gap-2">
                        <span v-if="showFilters" class="flex items-center gap-1">
                            <EyeOff class="w-5 h-5" />
                            Esconder Filtro
                        </span>
                        <span v-else class="flex items-center gap-1">
                            <Eye class="w-5 h-5" />
                            Filtrar por Categorias
                        </span>
                    </div>
                </div>

                <div class="flex flex-col md:flex-row gap-4">
                    <!-- Filtros -->
                    <div class="w-full md:w-1/4" v-if="!isMobile || showFilters">
                        <div class="space-y-4">
                            <div v-for="(cat, indexCategoria) in categorias" :key="indexCategoria"
                                class="category-container" :class="{ 'py-1': !cat.showSubcategorias }">
                                <h5 class="font-semibold text-[#343434]/75 cursor-pointer flex items-center gap-2.5 text-[16px]"
                                    @click="toggleSubcategorias(indexCategoria)">
                                    {{ cat.nome }}
                                    <span class="toggle-icon">{{ cat.showSubcategorias ? '-' : '+' }}</span>
                                </h5>
                                <hr class="my-1 text-gray-300" />
                                <ul v-show="cat.showSubcategorias">
                                    <a href="#" v-for="(sub, indexSubcategoria) in cat.subcategorias"
                                        :key="indexSubcategoria"
                                        @click.prevent="filtrarPorSubcategoria(cat.id, sub.id, cat.slug, sub.slug)">
                                        <li class="text-[#343434]/40 hover:text-[#343434]/80 text-[16px] font-regular">
                                            {{ sub.nome }}
                                        </li>
                                    </a>
                                </ul>
                            </div>
                            <div v-if="categorias && categorias.length > 0" class="flex items-center justify-center bg-vermelho py-4 rounded-lg font-semibold text-white cursor-pointer">
                                <Calculator class="w-5 h-5 mr-2" />
                                <span @click="router.push({ name: 'calculadora-consumo' })">Calculadora de Rejuntes</span>
                            </div>
                        </div>
                    </div>

                    <!-- Produtos -->
                    <div class="w-full md:w-3/4" v-if="produtos !== null">
                        <div class="text-center h-1/2 flex flex-col justify-center items-center"
                            v-if="produtos.length === 0">
                            <Frown class="w-16 h-16 text-[#343434]/75 mb-4" />
                            <h2 class="text-[#343434]/75 font-bold text-capitalize text-2xl">Nenhum produto foi
                                encontrado.</h2>
                            <h4 class="text-[#343434]/40 mt-2">Não há produtos disponíveis para a categoria escolhida.
                            </h4>
                        </div>

                        <div class="text-center" v-if="produtos.length !== 0">
                            <h2 class="flex text-4xl justify-start w-full pb-12 text-[#343434]/75 font-semibold">
                                {{ categoriaSelecionada }}
                            </h2>
                        </div>

                        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" v-if="produtos.length !== 0">
                            <div v-for="produto in produtos" :key="produto.id" @click="redirectToProduto(produto.slug)"
                                class="text-center mb-4 cursor-pointer transition-transform duration-200 hover:scale-103">
                                <div class="image-container mx-auto">
                                    <img :src="produto.imagem_produto_path || produto.imagem"
                                        class="fixed-size-image mb-2" :alt="produto.nome" />
                                </div>
                                <h4 class="font-bold text-sm md:text-base">{{ produto.nome }}</h4>
                                <p class="truncate font-regular text-gray-500 text-xs md:text-sm">{{ produto.sufixo }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { Calculator, Eye, EyeOff, Frown } from 'lucide-vue-next'
import { useLoading } from 'vue-loading-overlay'

const route = useRoute()
const router = useRouter()
const loaded = ref(false)
const categoriaSelecionada = ref('Produtos')
const produtos = ref(null)
const isMobile = ref(false)
const showFilters = ref(false)
const $loading = useLoading()
const categorias = ref([])

const fetchProducts = async () => {
    const loader = $loading.show()
    try {
        let url = `/api/produtos/categoria/${route.params.categoriaSlug}`
        if (route.params.subcategoriaSlug) {
            url += `/${route.params.subcategoriaSlug}`
        }

        const response = await axios.get(url)
        produtos.value = response.data.produtos || []
        categoriaSelecionada.value = response.data.categoria || 'Produtos'
    } catch (error) {
        console.error('Erro ao buscar produtos:', error)
        produtos.value = []
    } finally {
        loader.hide()
        loaded.value = true
    }
}


const fetchCategoriasSubcategorias = async () => {
    try {
        const response = await axios.get('/api/produtos/categorias')
        categorias.value = response.data || []
        categorias.value.forEach(cat => {
            cat.showSubcategorias = false
        })
    } catch (error) {
        console.error('Erro ao buscar categorias:', error)
        categorias.value = []
    }
}

const redirectToProduto = (slug) => {
    if (slug) {
        router.push({ name: 'produto', params: { slug } })
    }
}

const toggleSubcategorias = (index) => {
    categorias.value[index].showSubcategorias = !categorias.value[index].showSubcategorias
}

const checkWindowSize = () => {
    isMobile.value = window.innerWidth < 768
}

const toggleFilters = () => {
    showFilters.value = !showFilters.value
}

const filtrarPorSubcategoria = (categoriaId, subcategoriaId, categoriaSlug, subcategoriaSlug) => {
    router.push({
        name: 'produtos-subcategoria',
        params: { categoriaSlug, subcategoriaSlug }
    })
}

const initialize = async () => {
    checkWindowSize()
    window.addEventListener('resize', checkWindowSize)

    if (route.params.categoriaSlug) {
        await fetchProducts()
        await fetchCategoriasSubcategorias()
    } else {
        produtos.value = []
        loaded.value = true // fallback se não houver categoria
    }
}

onMounted(initialize)

onUnmounted(() => {
    window.removeEventListener('resize', checkWindowSize)
})
</script>

<style scoped>
.image-container {
    width: 150px;
    height: 150px;
    overflow: hidden;
    display: inline-block;
    border-radius: 8px;
}

.fixed-size-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.category-container {
    margin-bottom: 1rem;
}

.toggle-icon {
    font-size: 20px;
    font-weight: bold;
    transition: transform 0.3s ease;
}

@media (max-width: 768px) {
    .image-container {
        width: 120px;
        height: 120px;
    }
}
</style>
